import { Box, Stack, Typography } from '@mui/material';

export interface HowItWorksStep {
  /** What the reader does, or what the thing is. */
  title: string;
  /** One line of how it behaves or why it matters. */
  body: string;
  /**
   * `id` of the element this panel describes. Given one, the panel becomes a
   * button that scrolls its target into view — so a step is a way INTO the
   * step, not just a label for it. Panels with no on-page target (a step that
   * happens in a dialog, say) stay inert.
   */
  targetId?: string;
}

/**
 * Scroll a step's target into view and hand it focus, so the keyboard and a
 * screen reader land where the click sent the eye. Targets carry
 * `tabIndex={-1}` for exactly this. Honours `prefers-reduced-motion` by
 * jumping instead of gliding; a missing target is a no-op rather than a throw.
 * @param id - The target element id.
 */
function scrollToStep(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduced = window.matchMedia?.(
    '(prefers-reduced-motion: reduce)',
  )?.matches;
  el.scrollIntoView({
    behavior: reduced ? 'auto' : 'smooth',
    block: 'center',
  });
  el.focus({ preventScroll: true });
}

/**
 * The numbered "how this works" panels.
 *
 * Extracted from the processor landing page's section shell so the scheduling
 * wizard can open on the SAME explainer a producer met on the landing page,
 * rather than a lookalike that drifts from it. It also gives the piece a home
 * outside `buyer-marketplace`, which is being retired — the landing sections
 * that survive will need somewhere to live anyway.
 * @param root0 - Props.
 * @param root0.steps - The steps, in order.
 * @param root0.numbered - Number the panels rather than bulleting them.
 * @param root0.sx - Layout overrides for the grid.
 * @returns The panels, or null when there are none.
 */
export function HowItWorksSteps({
  steps,
  numbered = false,
  startNumber = 1,
  flatOnMobile = false,
}: {
  steps: HowItWorksStep[];
  numbered?: boolean;
  /**
   * Number the first panel from here rather than from 1. A wizard shows one
   * panel per step, and that panel has to carry the step's real position —
   * every screen saying "1" would be worse than no number at all.
   */
  startNumber?: number;
  /**
   * On phones, drop the panel chrome — border, fill, radius and the gap
   * between panels — so the steps read as a plain list. Desktop is unchanged.
   */
  flatOnMobile?: boolean;
}) {
  if (steps.length === 0) return null;
  const points = steps;
  return (
    <Box
      sx={{
        // No margin of its own. It used to carry `mt: 2.25` for the landing
        // page, where it follows a description — but in the booking wizard it
        // is the first child of a spaced Stack, so that margin stacked on the
        // dialog's own padding and left the panel with noticeably more room
        // above it than below. Spacing belongs to whoever composes it.
        display: 'grid',
        gap: flatOnMobile ? { xs: 0, sm: 1.25 } : 1.25,
        gridTemplateColumns: {
          xs: '1fr',
          md: `repeat(${Math.min(points.length, 3)}, 1fr)`,
        },
      }}
    >
      {points.map((point, i) => (
        <Stack
          key={point.title}
          direction="row"
          gap={1.25}
          {...(point.targetId
            ? {
                component: 'button',
                type: 'button',
                onClick: () => scrollToStep(point.targetId as string),
              }
            : {})}
          sx={(theme) => ({
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2.5,
            p: 1.5,
            textAlign: 'left',
            ...(flatOnMobile
              ? {
                  [theme.breakpoints.down('sm')]: {
                    bgcolor: 'transparent',
                    border: 0,
                    borderRadius: 0,
                    px: 0,
                    py: 1,
                  },
                }
              : {}),
            ...(point.targetId
              ? {
                  // Reset the button element back to a panel, then give it
                  // the affordances a panel-sized target needs.
                  font: 'inherit',
                  color: 'inherit',
                  width: '100%',
                  cursor: 'pointer',
                  transition: 'border-color .2s, background-color .2s',
                  '&:hover': {
                    borderColor: 'primary.light',
                    bgcolor: 'background.paper',
                  },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                  },
                }
              : {}),
          })}
        >
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: numbered ? 24 : 8,
              height: numbered ? 24 : 8,
              mt: numbered ? 0 : 0.75,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {numbered ? startNumber + i : null}
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {point.title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25, lineHeight: 1.55 }}
            >
              {point.body}
            </Typography>
          </Box>
        </Stack>
      ))}
    </Box>
  );
}
